import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collectionGroup, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { CheckCircle2, ArrowRight, ListOrdered, Printer, ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { WeeklyOrder, OrderItem } from './delivery_start';
import lang from '../language/Eng_Aust.json';
import { getNextMondayDateString, getWeekDays, formatDate, getMonday } from './utils';
import DeliveryInvoiceSection from './delivery_invoice';

const PageContainer = styled.div`
  box-sizing: border-box;
  min-height: 100vh;
  background-color: #FDFBF9;
  padding: 40px 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    padding: 24px 16px;
    background-color: white;
  }

  @media print {
    background-color: white;
    padding: 0;
    .no-print {
      display: none !important;
    }
  }
`;

const ContentWrapper = styled.div`
  box-sizing: border-box;
  background: white;
  border-radius: 24px;
  padding: 40px;
  width: 100%;
  max-width: 1200px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.03);
  border: 1px solid rgba(0,0,0,0.05);
  text-align: center;

  @media (max-width: 768px) {
    padding: 0;
    border-radius: 0;
    min-height: auto;
    border: none;
    box-shadow: none;
    background: transparent;
  }
`;

const SuccessIconWrapper = styled.div`
  color: #4CAF50;
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
`;

const Title = styled.h1`
  font-size: 32px;
  color: #1A1A1A;
  margin: 0 0 12px 0;
  font-weight: 800;
  letter-spacing: -0.5px;

  @media print {
    display: none !important;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 32px 0;

  @media print {
    display: none !important;
  }
`;

const WeekNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 32px;

  @media print {
    margin-bottom: 16px;
  }
`;

const NavButton = styled.button`
  background: white;
  border: 1px solid #EAEAEA;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #333;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F5F5F5;
  }
`;

const WeekLabel = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1A1A1A;
`;

const OrderDetailsCard = styled.div`
  background: #FDFDFD;
  border: 1px solid #EAEAEA;
  border-radius: 16px;
  padding: 24px;
  text-align: left;
  margin-bottom: 32px;

  @media print {
    page-break-inside: avoid;
    box-shadow: none;
    border: 1px solid #ccc;
    margin-bottom: 20px;
    padding: 12px;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 15px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  color: #666;
`;

const DetailValue = styled.span`
  font-weight: 600;
  color: #1A1A1A;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #EAEAEA;
  margin: 20px 0;
`;

const ItemList = styled.div`
  margin-top: 16px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 15px;
  color: #333;

  @media print {
    margin-bottom: 4px;
    font-size: 9pt;
    gap: 6px;
  }
`;




const ItemQuantity = styled.span`
  font-weight: 700;
  color: #937A3B;
  min-width: 24px;

  @media print {
    color: #000;
    font-size: 9pt;
    min-width: 20px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  text-align: left;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  @media print {
    display: none !important;
  }
`;

const PrintBranchCard = styled.div`
  display: none;

  @media print {
    display: block;
    page-break-after: always;
    page-break-inside: avoid;
    padding: 20px 24px;
    font-family: 'Inter', Arial, sans-serif;
  }

  &:last-child {
    @media print {
      page-break-after: auto;
    }
  }
`;

const PrintBranchHeader = styled.div`
  display: none;

  @media print {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    border-bottom: 3px solid #000;
    padding-bottom: 10px;
    margin-bottom: 18px;
  }
`;

const PrintBranchName = styled.h2`
  display: none;

  @media print {
    display: block;
    font-size: 20pt;
    font-weight: 900;
    margin: 0;
    color: #000;
    letter-spacing: -0.5px;
  }
`;

const PrintWeekLabel = styled.span`
  display: none;

  @media print {
    display: block;
    font-size: 11pt;
    color: #444;
    font-weight: 500;
  }
`;

const PrintDayBlock = styled.div`
  display: none;

  @media print {
    display: block;
    margin-bottom: 14px;
    break-inside: avoid;
  }
`;

const PrintDayHeader = styled.div`
  display: none;

  @media print {
    display: block;
    font-size: 11pt;
    font-weight: 800;
    color: #000;
    background: #f0f0f0;
    padding: 4px 8px;
    margin-bottom: 6px;
    border-radius: 2px;
  }
`;

const PrintItemLine = styled.div`
  display: none;

  @media print {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    font-size: 10.5pt;
    line-height: 1.5;
    border-bottom: 1px solid #e8e8e8;
    color: #111;
  }

  &:last-child {
    @media print {
      border-bottom: none;
    }
  }
`;

const PrintCheckbox = styled.span`
  display: none;

  @media print {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 1.5px solid #333;
    border-radius: 2px;
    flex-shrink: 0;
    margin-right: 2px;
  }
`;

const PrintQty = styled.span`
  display: none;

  @media print {
    display: inline-block;
    font-weight: 700;
    font-size: 10.5pt;
    min-width: 28px;
    color: #000;
  }
`;

const PrintItemName = styled.span`
  display: none;

  @media print {
    display: inline;
    font-size: 10.5pt;
    color: #111;
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  background-color: #FCE17A;
  color: #1A1A1A;
  border: none;
  border-radius: 12px;
  padding: 16px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #F8D654;
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  background-color: #FFF;
  color: #333;
  border: 1px solid #EAEAEA;
  border-radius: 12px;
  padding: 16px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #F5F5F5;
    transform: translateY(-2px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #666;
`;

const LogoutButton = styled.button`
  margin-top: 20px;
  background: none;
  border: none;
  color: #666;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s;
  padding: 16px;

  &:hover {
    color: #333;
  }
`;

const ScrollToTopButton = styled.button<{ $visible: boolean }>`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 50px;
  height: 50px;
  border-radius: 25px;
  background-color: #1A1A1A;
  color: white;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$visible ? '0' : '20px'});
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;

  &:hover {
    background-color: #333;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #FCE17A;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 16px;
`;

const DaySection = styled.div`
  margin-bottom: 24px;

  @media print {
    margin-bottom: 10px;
  }
`;

const DayTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #937A3B;
  border-bottom: 1px solid #FFF2D1;
  padding-bottom: 6px;
  margin-bottom: 12px;
  
  @media print {
    color: #000;
    border-bottom: 1px solid #000;
    font-size: 10pt;
    padding-bottom: 2px;
    margin-bottom: 4px;
  }
`;

const DeliveryMain: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState<WeeklyOrder[]>([]);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [selectedWeek, setSelectedWeek] = useState<string>(() => searchParams.get('week') || getNextMondayDateString());

  const weekDays = getWeekDays(selectedWeek);

  const handlePrevWeek = () => {
    const [y, m, d] = selectedWeek.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() - 7);
    setSelectedWeek(formatDate(date));
  };

  const handleNextWeek = () => {
    const [y, m, d] = selectedWeek.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + 7);
    setSelectedWeek(formatDate(date));
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/delivery/login');
      } else {
        setCurrentUserEmail(user.email);
        setIsCheckingAuth(false);
      }
    });

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      unsubscribe();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [navigate]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrintOrders = () => {
    document.body.classList.remove('print-invoices');
    window.print();
  };

  const handlePrintInvoices = () => {
    const cleanup = () => document.body.classList.remove('print-invoices');
    document.body.classList.add('print-invoices');
    window.addEventListener('afterprint', cleanup, { once: true });
    window.print();
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/delivery/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    if (isCheckingAuth || !currentUserEmail) return;

    setIsLoadingData(true);
    const branchesRef = collectionGroup(db, 'branches');
    // Keep open order pages in sync with edits saved by any branch.
    return onSnapshot(branchesRef, (querySnapshot) => {
      let fetchedOrders: WeeklyOrder[] = [];
      querySnapshot.forEach((doc) => {
        fetchedOrders.push({ id: doc.id, ...doc.data() } as WeeklyOrder);
      });

      // Use the same default ordering week as the branch order form.
      fetchedOrders = fetchedOrders.filter(o => o.weekStartDate === selectedWeek);

      if (currentUserEmail.toLowerCase() !== 'butkitchen@butteredsydney.com.au') {
        let branchId = 'unknown';
        if (currentUserEmail.toLowerCase() === 'hornsbymanager@firestone-sydney.com.au') {
          branchId = 'firestone-hornsby';
        } else if (currentUserEmail.toLowerCase() === 'chatswood@vuzahospitality.com.au') {
          branchId = 'chatswood';
        } else if (currentUserEmail.toLowerCase() === 'firestoneeastwood@firestone-sydney.com.au') {
          branchId = 'firestone-eastwood';
        }
        fetchedOrders = fetchedOrders.filter(o => o.branchId === branchId);
      }

      fetchedOrders.sort((a, b) => {
        const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return timeB - timeA;
      });

      setOrders(fetchedOrders);
      setIsLoadingData(false);
    }, (error) => {
      console.error('Error subscribing to orders:', error);
      setIsLoadingData(false);
    });
  }, [isCheckingAuth, currentUserEmail, selectedWeek]);

  if (isCheckingAuth || isLoadingData || !selectedWeek) {
    return (
      <PageContainer style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
        <Spinner />
        <h2 style={{ color: '#666', fontSize: '18px', fontWeight: 500 }}>{lang.main.loading}</h2>
      </PageContainer>
    );
  }

  const isManager = currentUserEmail === 'butkitchen@butteredsydney.com.au';
  const ALL_BRANCHES = ['Firestone Eastwood', 'Chatswood', 'Firestone Hornsby'];
  const invoiceOrders = ALL_BRANCHES
    .map(branchName => orders.find(order => order.branchName === branchName))
    .filter((order): order is WeeklyOrder => Boolean(order));
  const showPreviousWeekButton = isManager
    && orders.length === 0
    && selectedWeek === getNextMondayDateString();

  const renderOrderDetails = (order: WeeklyOrder) => {
    return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(dayName => {
      const dayData = weekDays.find(d => d.dayName === dayName);
      const items = order.dailyItems?.[dayName] || [];
      
      if (items.length === 0) return null;

      return (
        <DaySection key={dayName}>
          <DayTitle>{dayName} ({dayData?.shortDate})</DayTitle>
          <ItemList>
            {items.map((item: OrderItem) => (
              <ItemRow key={item.id}>
                <ItemQuantity>{item.quantity}x</ItemQuantity>
                <span>{item.name}</span>
              </ItemRow>
            ))}
          </ItemList>
        </DaySection>
      );
    });
  };

  return (
    <PageContainer>
      <ContentWrapper>
        {!isManager && (
          <SuccessIconWrapper className="no-print">
            <CheckCircle2 size={64} strokeWidth={1.5} />
          </SuccessIconWrapper>
        )}
        
        <Title>{isManager ? 'Weekly Orders Overview' : 'Weekly Order Confirmed'}</Title>
        <Subtitle>
          {isManager 
            ? 'Review and manage the weekly orders from all branches.' 
            : 'Your weekly order has been successfully placed.'}
        </Subtitle>

        <WeekNavigation className="no-print">
          <NavButton onClick={handlePrevWeek}>
            <ChevronLeft size={20} />
          </NavButton>
          <WeekLabel>
            Week of {weekDays[0].shortDate} - {weekDays[6].shortDate}
          </WeekLabel>
          <NavButton onClick={handleNextWeek}>
            <ChevronRight size={20} />
          </NavButton>
        </WeekNavigation>

        {isManager && (
          <OrdersGrid>
            {ALL_BRANCHES.map((branchName) => {
              const order = orders.find(o => o.branchName === branchName);
              
              return (
                <OrderDetailsCard key={branchName}>
                  <DetailRow style={{ borderBottom: '2px solid #F0F0F0', paddingBottom: '16px', marginBottom: '16px' }}>
                    <DetailLabel style={{ fontSize: '18px', color: '#1A1A1A', fontWeight: 700 }}>{branchName}</DetailLabel>
                  </DetailRow>

                  {order ? (
                    renderOrderDetails(order)
                  ) : (
                    <EmptyState style={{ padding: '40px 0' }}>
                      <span style={{ fontStyle: 'italic', color: '#999' }}>Order not yet placed</span>
                    </EmptyState>
                  )}
                  
                </OrderDetailsCard>
              );
            })}
          </OrdersGrid>
        )}

        {/* 프린트 전용: 매장 × 요일별 1페이지 레이아웃 */}
        {isManager && ALL_BRANCHES.map((branchName) => {
          const order = orders.find(o => o.branchName === branchName);
          const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

          return days.map(dayName => {
            const dayData = weekDays.find(d => d.dayName === dayName);
            const items = order?.dailyItems?.[dayName] || [];
            if (items.length === 0) return null;

            return (
              <PrintBranchCard className="order-print-card" key={`print-${branchName}-${dayName}`}>
                <PrintBranchHeader>
                  <PrintBranchName>{branchName}</PrintBranchName>
                  <PrintWeekLabel>Week of {weekDays[0].shortDate} – {weekDays[6].shortDate}</PrintWeekLabel>
                </PrintBranchHeader>

                <PrintDayHeader>{dayName}  ·  {dayData?.shortDate}</PrintDayHeader>

                {items.map((item: OrderItem) => (
                  <PrintItemLine key={item.id}>
                    <PrintCheckbox />
                    <PrintQty>{item.quantity}×</PrintQty>
                    <PrintItemName>{item.name}</PrintItemName>
                  </PrintItemLine>
                ))}
              </PrintBranchCard>
            );
          });
        })}

        {!isManager && orders.map((order, idx) => (
          <OrderDetailsCard key={order.id || idx} style={{ marginBottom: '24px' }}>
            <DetailRow>
              <DetailLabel>Branch</DetailLabel>
              <DetailValue>{order.branchName}</DetailValue>
            </DetailRow>
            <Divider />

            {renderOrderDetails(order)}
            
          </OrderDetailsCard>
        ))}

        {!isManager && orders.length === 0 && (
          <EmptyState>
            <h3 style={{ color: '#333' }}>No Orders Found</h3>
            <p>There are no weekly orders submitted for this week.</p>
          </EmptyState>
        )}

        <ButtonGroup className="no-print">
          {isManager && (
            <SecondaryButton onClick={handlePrintOrders}>
              <Printer size={18} /> Print Orders
            </SecondaryButton>
          )}
          {isManager && invoiceOrders.length > 0 && (
            <SecondaryButton onClick={handlePrintInvoices}>
              <Printer size={18} /> Print Invoices
            </SecondaryButton>
          )}
          {showPreviousWeekButton && (
            <PrimaryButton onClick={handlePrevWeek}>
              <ChevronLeft size={18} /> View Previous Week Orders
            </PrimaryButton>
          )}
          <SecondaryButton onClick={() => navigate('/delivery/list')}>
            <ListOrdered size={18} /> View History
          </SecondaryButton>
          {!isManager && (
            <PrimaryButton onClick={() => navigate('/delivery/start')}>
              <ArrowRight size={18} /> Edit / New Order
            </PrimaryButton>
          )}
        </ButtonGroup>

        {isManager && invoiceOrders.length > 0 && (
          <DeliveryInvoiceSection
            orders={invoiceOrders}
            weekStart={weekDays[0].shortDate}
            weekEnd={weekDays[6].shortDate}
          />
        )}

        <div className="no-print" style={{ textAlign: 'center', width: '100%', marginTop: '40px', paddingBottom: '60px' }}>
          <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
        </div>
      </ContentWrapper>
      <ScrollToTopButton className="no-print" $visible={showScrollTop} onClick={scrollToTop} aria-label="Scroll to top">
        <ArrowUp size={20} />
      </ScrollToTopButton>

      <style>{`
        @media print {
          .print-only-heading {
            display: block !important;
          }
          body.print-invoices .order-print-card {
            display: none !important;
          }
          /* 화면용 OrdersGrid, OrderDetailsCard, 기타 섹션 숨기기 */
          /* PrintBranchCard만 표시 (styled-component로 제어) */
          body {
            margin: 0;
            padding: 0;
          }
          @page {
            size: A4;
            margin: 10mm 12mm;
          }
        }
      `}</style>
    </PageContainer>
  );
};

export default DeliveryMain;
